import { Pagination, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { hwApi, subjectsApi } from '../../../api';
import copySvg from '../../../assets/img/copy.svg';
import { getStudents, getTeachers } from '../../../Common/Database';
import { checklistTablePopupsNames } from '../../../constants/checklistTablePopupsNames';
import { tabs } from '../../../constants/checlistTableTabs';
import { tableSize } from '../../../constants/tableSize';
import { Option } from '../../../shared/types';
import { HW, PersonOption } from '../../../shared/types/works';
import { ItemActionModal } from '../../../shared/ui/ItemActionModal';
import { LoadingError } from '../../LoadingError';
import HomeworkFilters from '../HomeworkFilters';
import TableTabs from '../TableTabs';

const adminTabs = [
  { tabName: 'library', tabKey: tabs.all },
  { tabName: 'For Consideration', tabKey: tabs.consideration },
  { tabName: 'Templates', tabKey: tabs.template },
];

export default function HomeworkTableAdmin() {
  const {
    t,
    i18n: { language: platformLang },
  } = useTranslation();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabQuery = searchParams.get('tab');

  const [hws, setHws] = useState<HW[]>([]);
  const [hwsForShow, setHwsForShow] = useState<HW[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemovingLoading, setIsRemovingLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState<string>('');
  const [idForManipulation, setIdForManipulation] = useState<string>('');
  const [tab, setTab] = useState<string>(tabs.all);
  const [page, setPage] = useState(1);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState<Option[] | null>(null);
  const [click, setClick] = useState(false);

  const [subjectFilter, setSubjectFilter] = useState<Option | null>(null);
  const [studentFilter, setStudentFilter] = useState<PersonOption | null>(null);
  const [teacherFilter, setTeacherFilter] = useState<PersonOption | null>(null);
  const [classFilter, setClassFilter] = useState<string | null>(null);

  useEffect(() => {
    setTab(tabQuery || tabs.all);
  }, [tabQuery]);

  useEffect(() => {
    const updateSubjects = (subjects: Option[]) => setSubjects(subjects);
    const unsubscribe = subjectsApi.subscribeToChanges(updateSubjects);

    return () => {
      unsubscribe();
    };
  }, []);

  const fetchTeachersListData = async () => {
    const teacherList = await getTeachers();
    if (teacherList) {
      const mapList = Object.values(teacherList);
      setTeachers(mapList);
    }
  };

  useEffect(() => {
    if (hwsForShow.length === 0 && page > 1) {
      setPage(page - 1);
    }
  }, [hwsForShow]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      try {
        const res = getStudents();
        res.then(resp => {
          const arrStudents = Object.values(resp);
          setStudents(arrStudents);
        });
      } catch (error) {
        console.log(error);
      }

      setIsLoading(false);
    };

    init();

    fetchTeachersListData();
  }, []);

  useEffect(() => {
    const unsubscribe = hwApi.subscribeToAdminHWs({ setNewHWs: setHws });
    setIsLoading(false);

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    setIsLoading(true);

    if (tab === tabs.consideration) {
      const unsubscribe = hwApi.subscribeToAdminConsiderationHWs({
        setNewHWs: setHws,
        classFilter,
        subjectFilter: subjectFilter?.id,
      });

      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.template) {
      const unsubscribe = hwApi.subscribeToTemplateHWs({
        setNewHWs: setHws,
        classFilter,
        subjectFilter: subjectFilter?.id,
      });

      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.all) {
      const unsubscribe = hwApi.subscribeToAdminHWs({
        setNewHWs: setHws,
        classFilter,
        subjectFilter: subjectFilter?.id,
        teacherFilter,
        studentFilter,
      });
      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
  }, [click, tab]);

  useEffect(() => {
    setIsLoading(true);

    if (tab === tabs.consideration) {
      const unsubscribe = hwApi.subscribeToAdminConsiderationHWs({ setNewHWs: setHws });

      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.template) {
      const unsubscribe = hwApi.subscribeToTemplateHWs({ setNewHWs: setHws });

      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.all) {
      const unsubscribe = hwApi.subscribeToAdminHWs({ setNewHWs: setHws });
      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
  }, [tab]);

  useEffect(() => {
    const start = (page - 1) * tableSize;
    const end = start + tableSize;

    setHwsForShow(hws.slice(start, end));
  }, [hws, page, tab]);

  const handleNavigateToChecklistUpdate = (id: string) => {
    navigate('/hw-update', { state: { hwId: id } });
  };

  const handleDeleteICL = async (id: string) => {
    setIsRemovingLoading(true);

    try {
      await hwApi.removeHW(id);
    } catch (e) {
      console.log(e);
      toast.error(t('Something went wrong. Try again!'));
    }

    toast.success(t('Successfully removed!'));
    setIsModalOpen('');
    setIsRemovingLoading(false);
  };

  const handleOpenDeletePopup = (id: string) => {
    setIsModalOpen(checklistTablePopupsNames.delete);
    setIdForManipulation(id);
  };

  const handleConsiderTemplate = async (id: string, accept: boolean) => {
    const targetIcl = hws.find(it => it.id === id);

    if (targetIcl) {
      if (Object.prototype.hasOwnProperty.call(targetIcl, 'isConsideration')) {
        targetIcl['isConsideration'] = false;

        if (accept) {
          await hwApi.copyHWToAdmin(targetIcl).then(() => {
            setTab(tabs.all);
            toast.success(t('Successfully added!'));
          });
        } else {
          toast.success(t('Successfully denied!'));
        }
      }

      await hwApi.updateHW(targetIcl);
    }
  };

  const handlePopupActions = () => {
    switch (isModalOpen) {
      case 'delete':
        handleDeleteICL(idForManipulation);
        break;
      case 'copyAsAdmin':
        handleConsiderTemplate(idForManipulation, true);
        break;
      case 'denied':
        handleConsiderTemplate(idForManipulation, false);
    }

    setIsModalOpen('');
  };

  const actionPopupButtonTitle = useMemo(() => {
    switch (isModalOpen) {
      case checklistTablePopupsNames.delete:
        return 'delete';
      case checklistTablePopupsNames.copyAsAdmin:
        return 'copyAsTetherButton';
      case checklistTablePopupsNames.denied:
        return 'deniedButton';
      default:
        return '';
    }
  }, [isModalOpen]);

  const actionPopupTitle = useMemo(() => {
    switch (isModalOpen) {
      case checklistTablePopupsNames.delete:
        return 'want to delete hw';
      case checklistTablePopupsNames.copyAsAdmin:
        return 'copyAsAdmin';
      case checklistTablePopupsNames.denied:
        return 'denied';
      default:
        return '';
    }
  }, [isModalOpen]);

  const handleOpenCopyPopup = (id: string) => {
    setIsModalOpen(checklistTablePopupsNames.copyAsAdmin);
    setIdForManipulation(id);
  };

  const handleOpenDeniedPopup = (id: string) => {
    setIsModalOpen(checklistTablePopupsNames.denied);
    setIdForManipulation(id);
  };

  const renderConsiderationButton = (id: string, isConsideration: boolean, isTemplate: boolean) => {
    if (!isTemplate && isConsideration) {
      return (
        <div className={'flex items-center gap-2'}>
          <button className="btn btn-primary more" onClick={() => handleOpenCopyPopup(id)}>
            {t('Accept')}
          </button>
          <button className="btn more" onClick={() => handleOpenDeniedPopup(id)}>
            {t('Denied')}
          </button>
        </div>
      );
    }

    if (!isTemplate && !isConsideration) {
      return (
        <Tooltip
          title={t('copyAsTether')}
          componentsProps={{
            popper: {
              sx: {
                ['& .MuiTooltip-arrow']: {
                  color: '#9CDFFC',
                },
                ['& .MuiTooltip-tooltip']: {
                  backgroundColor: '#9CDFFC',
                  color: '#000000',
                  fontSize: '14px',
                  padding: '14px',
                  borderRadius: '8px',
                },
              },
            },
          }}
          arrow
        >
          <button
            className="btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px]"
            onClick={() => handleOpenCopyPopup(id)}
            title={t('copyAsTether')}
          >
            <img src={copySvg} alt="copy" />
          </button>
        </Tooltip>
      );
    }
  };

  const handleChangePage = async (newPage: number) => {
    setPage(newPage);
  };

  const handleApplyFilters = () => {
    setClick(!click);
  };

  const handleResetFilters = () => {
    setClick(!click);
    setTeacherFilter(null);
    setStudentFilter(null);
    setSubjectFilter(null);
    setClassFilter(null);
  };

  const handleChangeTab = (tab: string) => {
    setTab(tab);
    handleResetFilters();
    setPage(1);
  };

  const getFIOFromEmail = (email: string) => {
    for (const student of students) {
      if (student.email?.replace(/\./g, '') === email) {
        return student.FIO;
      }
    }

    for (const teacher of teachers) {
      if (teacher.email?.replace(/\./g, '') === email) {
        return teacher.FIO;
      }
    }

    return email;
  };

  const renderRows = () => {
    return hwsForShow.map(
      ({ id, studentEmail, teacherEmail, subject, class: studentClass, isConsideration, isTemplate, title }) => {
        return (
          <TableRow key={id}>
            <TableCell
              sx={{
                maxWidth: 200,
                overflow: 'hidden',
                position: 'sticky',
                left: 0,
                backgroundColor: 'white',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>{studentClass}</TableCell>
            <TableCell>{platformLang === 'ua' ? subject.name : subject.ruName}</TableCell>
            {tab === tabs.all && <TableCell>{getFIOFromEmail(studentEmail)}</TableCell>}
            {tab === tabs.all && <TableCell>{getFIOFromEmail(teacherEmail)}</TableCell>}
            <TableCell>
              <div className="flex flex-row justify-end gap-[10px] [&>*]:!text-[16px] lg:[&>*]:!text-[18px]">
                <button className="btn btn-primary more" onClick={() => handleNavigateToChecklistUpdate(id)}>
                  {t('editing')}
                </button>
                {renderConsiderationButton(id, isConsideration ?? false, isTemplate)}
                {isTemplate && (
                  <Tooltip
                    title={t('deleteHomeWork')}
                    componentsProps={{
                      popper: {
                        sx: {
                          ['& .MuiTooltip-arrow']: {
                            color: '#9CDFFC',
                          },
                          ['& .MuiTooltip-tooltip']: {
                            backgroundColor: '#9CDFFC',
                            color: '#000000',
                            fontSize: '14px',
                            padding: '14px',
                            borderRadius: '8px',
                          },
                        },
                      },
                    }}
                    arrow
                  >
                    <button
                      title={t('deleteTask')}
                      className="btn more small h-[40px] w-[40px] rounded-[8px]"
                      onClick={() => handleOpenDeletePopup(id)}
                    >
                      <span className="icon icon-delete text-red-600" />
                    </button>
                  </Tooltip>
                )}
              </div>
            </TableCell>
          </TableRow>
        );
      },
    );
  };

  return (
    <LoadingError isLoading={isLoading && !hws}>
      <div>
        <div className="mb-[20px] flex gap-[8px] lg:block">
          <TableTabs setTab={handleChangeTab} tab={tab} configArr={adminTabs} />

          {students && teachers && subjects && (
            <HomeworkFilters
              subject={subjectFilter}
              student={studentFilter}
              teacher={teacherFilter}
              studentClass={classFilter}
              setClassFilter={setClassFilter}
              setStudentFilter={setStudentFilter}
              setTeacherFilter={setTeacherFilter}
              setSubjectFilter={setSubjectFilter}
              teachers={teachers}
              students={students}
              handleApply={handleApplyFilters}
              handleReset={handleResetFilters}
              click={click}
              subjects={subjects}
              tab={tab}
            />
          )}
        </div>

        <div className="mb-[20px] rounded-[24px] bg-white [box-shadow:10px_10px_0px_0px_#EBEAE7] lg:px-[20px] lg:py-[10px]">
          <TableContainer sx={{ borderRadius: '24px' }}>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      maxWidth: 200,
                      position: 'sticky',
                      left: 0,
                      backgroundColor: 'white',
                      borderTopLeftRadius: '24px',
                    }}
                  >
                    {t('title')}
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>{t('class')}</TableCell>
                  <TableCell>{t('subject')}</TableCell>
                  {tab === tabs.all && <TableCell>{t('alreadySend')}</TableCell>}
                  {tab === tabs.all && <TableCell>{t('Teacher')}</TableCell>}
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{renderRows()}</TableBody>
            </Table>
          </TableContainer>
        </div>

        <Stack alignItems="center">
          <Pagination
            sx={{
              '& .MuiPaginationItem-page.Mui-selected': {
                backgroundColor: 'white',
                border: '1px solid #52C7FA',
              },
              '& .MuiPaginationItem-page': {
                color: '#656D7B',
              },
            }}
            shape={'rounded'}
            page={page}
            onChange={(_, page) => handleChangePage(page)}
            count={Math.ceil(hws.length / tableSize)}
          />
        </Stack>

        <ItemActionModal
          isOpen={Boolean(isModalOpen)}
          close={() => setIsModalOpen('')}
          title={actionPopupTitle}
          onHandle={() => handlePopupActions()}
          isSubmitButtonDisabled={isRemovingLoading}
          buttonTitle={actionPopupButtonTitle}
        />
      </div>
    </LoadingError>
  );
}
